import { getDeviceId } from "../celery/celery";
import { getGlobalHeadersAsObject } from "../global-headers";

export interface CelTask {
	id: number;
	task_id: string;
	task_name: string;
	args: string;
	kwargs: Record<string, unknown>;
	created: string;
	done: string | null;
	error_acknowledged: string | null;
	display: string;
	meta: Record<string, unknown> | null;
}

/**
 * Fetch a single CelTask by its celery task_id.
 * Returns null if the task is not (yet) recorded — CelTask rows are written
 * by the @track_celery_task decorator when the worker picks the task up.
 */
export const getCelTaskById = async (taskId: string): Promise<CelTask | null> => {
	const res = await fetch(`/api/v2/celtask/${encodeURIComponent(taskId)}/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (res.status === 404) return null;
	if (!res.ok) throw Error(`Failed to fetch CelTask ${taskId}: ${res.status}`);
	return res.json();
};

/**
 * Record a TODO page visit ping.
 */
export const sendTodoPing = async (): Promise<void> => {
	const URL = "/api/v2/celtask/todo_ping/";
	const deviceId = await getDeviceId();
	await fetch(URL, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ device_id: deviceId }),
	});
};

/**
 * Mark a CelTask as done by setting the done timestamp.
 */
export const markCeleryTaskDone = async ({
	taskId,
}: {
	taskId: string;
}): Promise<CelTask> => {
	const URL = `/api/v2/celtask/${taskId}/`;

	return fetch(URL, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ done: new Date().toISOString() }),
	}).then(async (res) => {
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw Error(data.error || "Failed to mark task as done");
		}
		return res.json();
	});
};

/**
 * Acknowledge every unacknowledged FAILURE for a given task_name in the
 * current celery-tasks window. Returns the number newly acknowledged.
 */
export const acknowledgeCeleryErrorsByTaskName = async (
	taskName: string
): Promise<{ acknowledged: number }> => {
	const res = await fetch("/api/v2/celtask/acknowledge_by_task_name/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ task_name: taskName }),
	});
	if (!res.ok) {
		const data = await res.json().catch(() => ({}));
		throw Error(data.error || "Failed to acknowledge errors");
	}
	return res.json();
};

/**
 * Acknowledge a failed celery task. Server creates a CelTask row if one
 * doesn't exist (tasks not wrapped by @track_celery_task have no row yet).
 */
export const acknowledgeCeleryTaskError = async ({
	taskId,
}: {
	taskId: string;
}): Promise<CelTask> => {
	const URL = `/api/v2/celtask/${taskId}/acknowledge_error/`;
	return fetch(URL, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then(async (res) => {
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw Error(data.error || "Failed to acknowledge error");
		}
		return res.json();
	});
};

export interface CelTaskQueryParams {
	done__isnull?: boolean;
	assignment_id?: number;
	task_name?: string;
	task_name__in?: string[];
}

/**
 * Fetch CelTasks with optional filtering.
 */
export const getCelTasks = async (params?: CelTaskQueryParams): Promise<CelTask[]> => {
	const searchParams = new URLSearchParams();
	if (params?.done__isnull !== undefined) {
		searchParams.append("done__isnull", String(params.done__isnull));
	}
	if (params?.assignment_id !== undefined) {
		searchParams.append("assignment_id", String(params.assignment_id));
	}
	if (params?.task_name) {
		searchParams.append("task_name", params.task_name);
	}
	if (params?.task_name__in && params.task_name__in.length > 0) {
		searchParams.append("task_name__in", params.task_name__in.join(","));
	}

	const queryString = searchParams.toString();
	const URL = `/api/v2/celtask/${queryString ? `?${queryString}` : ""}`;

	return fetch(URL, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			throw Error("Failed to fetch CelTasks");
		}
		return res.json();
	});
};

// GradeConnectMode values from backend
export const GradeConnectMode = {
	SEARCH_AUTO_KEYWORDS: 11,
} as const;

/**
 * Check if an assignment has a relevant running task.
 */
export const hasRelevantRunningTask = (tasks: CelTask[]): boolean => {
	return filterRelevantTasks(tasks).length > 0;
};

/**
 * Filter tasks to only include relevant ones for an assignment.
 * - rebuild_gcq: filter by mode SEARCH_AUTO_KEYWORDS
 * - fill_llm_queue: always relevant (LLM is the only path)
 */
export const filterRelevantTasks = (tasks: CelTask[]): CelTask[] => {
	return tasks.filter((task) => {
		if (task.task_name === "core.tasks.rebuild_gcq") {
			return task.kwargs?.mode === GradeConnectMode.SEARCH_AUTO_KEYWORDS;
		}
		// fill_llm_queue and other tasks always relevant
		return true;
	});
};

/**
 * Fetch running tasks grouped by assignment ID.
 * Returns a map of assignment_id -> CelTask[]
 */
export const getRunningTasksByAssignment = async (
	taskNames?: string[]
): Promise<Record<number, CelTask[]>> => {
	const tasks = await getCelTasks({
		done__isnull: true,
		task_name__in: taskNames,
	});

	// Group by assignment_id from kwargs
	const grouped: Record<number, CelTask[]> = {};
	for (const task of tasks) {
		const assignmentId = task.kwargs?.assignment_id;
		if (typeof assignmentId === "number") {
			if (!grouped[assignmentId]) {
				grouped[assignmentId] = [];
			}
			grouped[assignmentId].push(task);
		}
	}

	return grouped;
};
